/**
 * One cart note, shared by the cart page and drawer.
 * Delegated handlers survive AJAX section replacement and duplicate asset tags.
 * Optional note failures keep the draft visible without blocking a purchase.
 */
(function () {
  if (window.__drhCartNoteInitialized) return;
  window.__drhCartNoteInitialized = true;

  const DEBOUNCE_MS = 800;
  const STATUS_CLEAR_MS = 1500;
  const SAVE_TIMEOUT_MS = 1500;
  const COUNTER_WARN_RATIO = 0.9;
  let saveTimer = null;
  let statusTimer = null;
  let pendingValue = null;
  let editedValue = null;
  let revision = 0;
  let cleanRevision = 0;
  let resumedSubmit = null;
  let inflight = null;
  let checkoutPending = false;
  let statusState = '';
  let noteUncertain = false;
  let failedRevision = -1;
  let latestCartCount = null;

  function wrappers() {
    return document.querySelectorAll('[data-cart-note]');
  }

  function renderStatus() {
    const displayedState = window.CartOperations?.isBlocked() ? 'purchase-error' : statusState;
    wrappers().forEach((wrapper) => {
      const status = wrapper.querySelector('[data-cart-note-status]');
      if (!status) return;
      const texts = {
        saving: status.dataset.textSaving || 'Wird gespeichert …',
        saved: status.dataset.textSaved || 'Gespeichert',
        error: status.dataset.textError || 'Wir konnten Ihre Anmerkung nicht bestätigen. Sie können trotzdem zur Kasse gehen.',
        'purchase-error': window.CartMessages?.uncertain || 'Wir konnten die Warenkorbänderung nicht bestätigen. Bitte laden Sie die Seite neu und prüfen Sie Ihren Warenkorb.',
      };
      status.textContent = texts[displayedState] || '';
      status.classList.toggle('cart-drawer__note-status--saved', displayedState === 'saved');
      status.classList.toggle('cart-drawer__note-status--error', displayedState === 'error' || displayedState === 'purchase-error');
    });
  }

  function setStatus(state) {
    clearTimeout(statusTimer);
    statusState = state;
    renderStatus();
    if (state === 'saved') statusTimer = setTimeout(() => setStatus(''), STATUS_CLEAR_MS);
  }

  function updateFields() {
    wrappers().forEach((wrapper) => {
      const input = wrapper.querySelector('[data-cart-note-input]');
      if (!input) return;
      if (editedValue !== null && input.value !== editedValue) input.value = editedValue;
      const counter = wrapper.querySelector('[data-cart-note-count]');
      const counterEl = wrapper.querySelector('[data-cart-note-counter]');
      if (counter && counterEl) {
        counter.textContent = input.value.length;
        const max = parseInt(counterEl.dataset.max, 10) || 0;
        counterEl.classList.toggle('cart-drawer__note-counter--warning', max > 0 && input.value.length >= max * COUNTER_WARN_RATIO);
      }
      const details = wrapper.querySelector('details');
      const open = wrapper.querySelector('[data-summary-open]');
      const close = wrapper.querySelector('[data-summary-close]');
      if (details && open && close) {
        open.hidden = details.open;
        close.hidden = !details.open;
      }
    });
    renderStatus();
  }

  function flushSave() {
    clearTimeout(saveTimer);
    saveTimer = null;
    if (inflight) return inflight;
    if (pendingValue === null) return Promise.resolve(true);
    if (noteUncertain || failedRevision === revision) return Promise.resolve(false);

    // One serialized queue for both fields. Edits arriving during a request
    // remain local drafts if checkout has already attempted the optional save.
    inflight = (async () => {
      while (pendingValue !== null) {
        const value = pendingValue;
        const savingRevision = revision;
        pendingValue = null;
        setStatus('saving');
        try {
          const ops = window.CartOperations;
          if (!ops) throw new Error('Cart operations unavailable');
          const saved = await ops.run(async (operation) => {
            const root = window.Shopify?.routes?.root || '/';
            try {
              const data = await operation.requestJSON(root + 'cart/update.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ note: value }),
              }, { timeoutMs: SAVE_TIMEOUT_MS });
              const confirmed = data?.note === null ? '' : data?.note;
              if (typeof confirmed !== 'string' || confirmed !== value) {
                operation.markUncertain();
                throw new Error('Note was not confirmed');
              }
            } catch (error) {
              if (operation.isUncertain()) noteUncertain = true;
              throw error;
            }
            return true;
          }, { purchase: false });
          if (!saved) throw new Error('Note save failed');
          cleanRevision = savingRevision;
          if (revision === savingRevision) editedValue = null;
          if (checkoutPending) break;
        } catch (_error) {
          failedRevision = savingRevision;
          if (pendingValue === null) pendingValue = value;
          setStatus('error');
          return false;
        }
      }
      setStatus(pendingValue === null ? 'saved' : 'error');
      return true;
    })().finally(() => {
      clearTimeout(saveTimer);
      saveTimer = null;
      inflight = null;
    });
    return inflight;
  }

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-cart-note-input]');
    if (!input || !input.closest('[data-cart-note]')) return;
    revision += 1;
    editedValue = input.value;
    pendingValue = editedValue;
    updateFields();
    clearTimeout(saveTimer);
    saveTimer = null;
    if (checkoutPending) {
      setStatus('error');
      return;
    }
    saveTimer = setTimeout(() => {
      if (!checkoutPending) void flushSave();
    }, DEBOUNCE_MS);
  });

  document.addEventListener('toggle', (event) => {
    if (event.target.matches('[data-cart-note] details')) updateFields();
  }, true);

  function beforeCheckout(event, resume) {
    const quantities = window.CartLineControls;
    quantities?.commitFocusedQuantity?.();
    const ops = window.CartOperations;
    if (ops?.isBlocked()) { event.preventDefault(); setStatus('purchase-error'); return; }
    if (pendingValue === null && !inflight && !quantities?.hasPendingUpdates() && !ops?.hasPending()) return;
    event.preventDefault();
    if (checkoutPending) return;
    checkoutPending = true;
    const initialQuantityFailures = quantities?.updateFailureCount?.() ?? 0;
    const initialPurchaseFailures = ops?.failureCount?.() ?? 0;
    let checkoutNote = inflight;
    let noteAttempted = !!checkoutNote;
    const drain = async () => {
      // Let the original native submit finish before requestSubmit can resume it.
      await new Promise((resolve) => setTimeout(resolve, 0));
      while (true) {
        const quantitySave = ops?.hasPending() ? ops.waitForIdle() : quantities?.hasPendingUpdates() ? quantities.waitForPendingUpdates() : true;
        let noteSave = checkoutNote || true;
        checkoutNote = null;
        if (!noteAttempted && (pendingValue !== null || inflight)) {
          noteAttempted = true;
          noteSave = flushSave();
        }
        const [, quantitySaved] = await Promise.all([noteSave, quantitySave]);
        if (!quantitySaved || ops?.isBlocked() || (quantities?.updateFailureCount?.() ?? 0) !== initialQuantityFailures
          || (ops?.failureCount?.() ?? 0) !== initialPurchaseFailures) return 'quantity-error';
        quantities?.commitFocusedQuantity?.();
        // Edits can start or finish while the other save is pending. Recheck
        // both queues and their failures before navigating in this same turn.
        if (quantities?.hasPendingUpdates() || ops?.hasPending() || (!noteAttempted && pendingValue !== null)) continue;
        if (pendingValue !== null) setStatus('error');
        resume();
        return 'complete';
      }
    };
    drain().finally(() => { checkoutPending = false; });
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-checkout-btn]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const surface = link.closest('[data-cart-page], [data-cart-drawer]');
    const sectionId = surface?.dataset.cartSectionId;
    const selector = surface?.hasAttribute('data-cart-page') ? '[data-cart-page]' : '[data-cart-drawer]';
    beforeCheckout(event, () => {
      if (latestCartCount === 0) return;
      let current = link.isConnected ? link : null;
      if (!current && sectionId) {
        const currentSurface = Array.from(document.querySelectorAll(selector))
          .find((item) => item.dataset.cartSectionId === sectionId);
        current = currentSurface?.querySelector('a[data-checkout-btn]');
      }
      if (current) window.location.assign(current.href);
    });
  });

  document.addEventListener('submit', (event) => {
    const button = event.submitter;
    if (event.target === resumedSubmit?.form && button === resumedSubmit.button) {
      resumedSubmit = null;
      return;
    }
    if (event.defaultPrevented || !button || !button.matches('[data-cart-checkout]')) return;
    const form = event.target;
    const surface = form.closest('[data-cart-page], [data-cart-drawer]');
    const kind = surface?.hasAttribute('data-cart-page') ? '[data-cart-page]' : '[data-cart-drawer]';
    const sectionId = surface?.dataset.cartSectionId;
    const formId = form.id;
    beforeCheckout(event, () => {
      let currentForm = form.isConnected ? form : null;
      if (!currentForm && formId) currentForm = document.getElementById(formId);
      if (!currentForm && sectionId) {
        const currentSurface = Array.from(document.querySelectorAll(kind))
          .find((item) => item.dataset.cartSectionId === sectionId);
        currentForm = currentSurface?.querySelector('[data-cart-checkout]')?.form;
      }
      const current = button.isConnected && button.form === currentForm
        ? button : currentForm?.querySelector('[data-cart-checkout]');
      if (latestCartCount === 0 || !currentForm?.isConnected || !current || current.disabled) return;
      // An unconfirmed AJAX note may already have reached Shopify. Do not
      // silently submit that draft a second time through the native cart form.
      const omitDraft = noteUncertain || revision !== cleanRevision || pendingValue !== null;
      const noteFields = omitDraft
        ? Array.from(currentForm.querySelectorAll('[data-cart-note-input][name="note"]')) : [];
      noteFields.forEach((field) => field.removeAttribute('name'));
      resumedSubmit = { form: currentForm, button: current };
      try { currentForm.requestSubmit(current); }
      finally {
        noteFields.forEach((field) => field.setAttribute('name', 'note'));
        resumedSubmit = null;
      }
    });
  });

  document.addEventListener('cart:operations-changed', renderStatus);

  document.addEventListener('cart:updated', (event) => {
    const data = event.detail;
    if (pendingValue === null && !inflight && cleanRevision === revision && (typeof data?.note === 'string' || data?.note === null)) {
      editedValue = null;
      wrappers().forEach((wrapper) => { const input = wrapper.querySelector('[data-cart-note-input]'); if (input) input.value = data.note || ''; });
    }
    const count = data && data.item_count;
    if (typeof count === 'number') latestCartCount = count;
    if (typeof count === 'number') wrappers().forEach((wrapper) => { wrapper.style.display = count === 0 ? 'none' : ''; });
    updateFields();
  });
  updateFields();
})();
